import { Link } from "@tanstack/react-router";

import {
  type BillingEntitlement,
  humanizePlanKey,
  formatCredits,
} from "../lib/billing";
import { Button, Inline, InlineError, Section, Stack, Text } from "../ui";

export function CheckoutPlanPicker({
  entitlement,
  selectedPlanKey,
  isPending,
  error,
  onConfirm,
}: {
  entitlement: BillingEntitlement;
  selectedPlanKey: string;
  isPending: boolean;
  error: string | null;
  onConfirm: () => void;
}) {
  return (
    <Section heading="Choose your plan">
      <Stack gap={4}>
        <div className="marker-checkout-plans" aria-label="Plans">
          {entitlement.available_plan_keys.map((planKey) => {
            const plan = entitlement.plan_catalog.find(
              (item) => item.plan_key === planKey,
            );
            return (
              <Link
                className="marker-checkout-plan"
                data-selected={selectedPlanKey === planKey}
                aria-current={selectedPlanKey === planKey ? "true" : undefined}
                key={planKey}
                to="/checkout"
                search={{ plan: planKey }}
                disabled={isPending}
              >
                <span className="marker-checkout-radio" aria-hidden="true" />
                <span className="marker-checkout-plan-name">
                  <Text as="span">
                    {plan?.display_name || humanizePlanKey(planKey)}
                  </Text>
                  {plan?.credit_allotment != null ? (
                    <Text as="span" variant="muted">
                      {formatCredits(plan.credit_allotment)} credits / month
                    </Text>
                  ) : null}
                </span>
                <span className="marker-checkout-price">
                  {plan
                    ? new Intl.NumberFormat(undefined, {
                        style: "currency",
                        currency: plan.currency,
                        // Whole-dollar plans read as "$250", not "$250.00".
                        maximumFractionDigits:
                          plan.monthly_price_cents % 100 === 0 ? 0 : 2,
                      }).format(plan.monthly_price_cents / 100)
                    : "At payment"}
                  {plan ? (
                    <Text as="span" variant="muted">
                      / month
                    </Text>
                  ) : null}
                </span>
              </Link>
            );
          })}
        </div>
        {error ? <InlineError>{error}</InlineError> : null}
        <Inline>
          <Button
            disabled={isPending || !selectedPlanKey}
            type="button"
            onClick={onConfirm}
          >
            {isPending ? "Preparing payment…" : "Continue to payment"}
          </Button>
        </Inline>
      </Stack>
    </Section>
  );
}
